import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IconName } from '@app/utils/enums/icon-enum';
import { Customer, CustomerUpdatePayload } from '@app/utils/interfaces/customer.interface';
import { CustomerService } from '../../services/customers.service';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { CountriesList, Country } from '@app/utils/interfaces/countries-list';
import { GetCountriesService } from '@app/pages/home-page/services/get-countries.service';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import {
  BaseEditForm,
  ValidatorsForForms,
} from '@app/components/shared/base-edit-form/base-edit-form';

@Component({
  selector: 'app-customer-edit-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatDialogModule,
    IconComponent,
  ],
  templateUrl: './customer-edit-form.component.html',
  styleUrls: ['./customer-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerEditFormComponent extends BaseEditForm implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CustomerEditFormComponent>);
  private readonly data = inject<Customer>(MAT_DIALOG_DATA);
  private readonly customerService = inject(CustomerService);
  private readonly countryService = inject(GetCountriesService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);

  public readonly IconName = IconName;
  public readonly loading = signal(false);

  protected countries: Country[] = [];
  protected selectedCountry: Country | null = null;

  public readonly validationErrors: Record<string, ValidatorsForForms[]> = {
    firstName: [
      { key: 'required', translation: 'customers.editCustomer.errors.firstNameInvalid' },
      { key: 'minlength', translation: 'customers.editCustomer.errors.firstNameInvalid' },
      { key: 'maxlength', translation: 'customers.editCustomer.errors.firstNameInvalid' },
      { key: 'pattern', translation: 'customers.editCustomer.errors.firstNameInvalid' },
    ],
    lastName: [
      { key: 'required', translation: 'customers.editCustomer.errors.lastNameInvalid' },
      { key: 'minlength', translation: 'customers.editCustomer.errors.lastNameInvalid' },
      { key: 'maxlength', translation: 'customers.editCustomer.errors.lastNameInvalid' },
      { key: 'pattern', translation: 'customers.editCustomer.errors.lastNameInvalid' },
    ],
    email: [
      { key: 'required', translation: 'customers.editCustomer.errors.emailInvalid' },
      { key: 'email', translation: 'customers.editCustomer.errors.emailInvalid' },
      { key: 'maxlength', translation: 'customers.editCustomer.errors.emailInvalid' },
    ],
    phoneNumber: [
      { key: 'required', translation: 'customers.editCustomer.errors.phoneNumberInvalid' },
      { key: 'pattern', translation: 'customers.editCustomer.errors.phoneNumberInvalid' },
    ],
    countryId: [{ key: 'required', translation: 'customers.editCustomer.errors.countryInvalid' }],
  };

  public form: FormGroup = this.fb.group({
    firstName: ['', this.getNameValidators()],
    lastName: ['', this.getNameValidators()],
    email: ['', this.getEmailValidators()],
    countryId: [null, [Validators.required]],
    phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9\-\s()]{5,20}$/)]],
    isActive: [false],
    remarks: [''],
    loyaltyId: [{ value: '', disabled: true }],
  });

  public ngOnInit(): void {
    this.form.patchValue({
      firstName: this.data.firstName ?? '',
      lastName: this.data.lastName ?? '',
      email: this.data.email ?? '',
      isActive: this.data.isActive ?? false,
      remarks: this.data.remarks ?? '',
      loyaltyId: this.data.loyaltyId ?? '',
    });

    this.form
      .get('countryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryId) => {
        this.selectedCountry = this.countries.find((country) => country.id === countryId) ?? null;
      });

    this.getCountries();
  }

  public getErrorMessageForCustomer(controlName: string): string {
    return super.getErrorMessage(this.form, controlName, this.validationErrors);
  }

  protected get hasPhoneError(): boolean {
    const phoneControl = this.form.get('phoneNumber');
    const countryControl = this.form.get('countryId');

    const phoneInvalid = !!phoneControl?.invalid && !!(phoneControl.dirty || phoneControl.touched);
    const countryInvalid =
      !!countryControl?.invalid && !!(countryControl.dirty || countryControl.touched);

    return phoneInvalid || countryInvalid;
  }

  protected getSelectedPhoneCode(): string {
    return this.selectedCountry?.phoneCode ?? '';
  }

  public onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    const payload: CustomerUpdatePayload = {
      firstName: raw.firstName,
      lastName: raw.lastName,
      email: raw.email,
      countryId: raw.countryId ?? undefined,
      phoneNumber: raw.phoneNumber,
      remarks: raw.remarks,
      isActive: raw.isActive,
    };

    this.loading.set(true);

    this.customerService
      .updateCustomerWithLoyaltyId(this.data.loyaltyId, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          this.dialogRef.close(false);
        },
      });
  }

  protected onCancel(): void {
    if (!this.hasChanges()) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.addPanelClass('hidden-dialog');

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    confirmRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close();
          return;
        }

        this.dialogRef.removePanelClass('hidden-dialog');
      });
  }

  private getCountries(): void {
    this.loading.set(true);

    this.countryService
      .getCountries()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: ({ data }: CountriesList) => {
          this.countries = data ?? [];
          this.patchPhoneData();
        },
        error: () => {
          this.dialog.open(ErrorModalComponent, {
            width: '400px',
            data: {
              message: 'customers.editCustomer.errors.countriesNotFound',
            },
          });
        },
      });
  }

  private patchPhoneData(): void {
    const storedCode = this.getStoredPhoneCountryCode();
    const storedNumber = this.getStoredPhoneNumber();

    const matchedCountry = this.countries.find((country) => country.phoneCode === storedCode);

    this.selectedCountry = matchedCountry ?? null;

    this.form.patchValue({
      countryId: matchedCountry?.id ?? null,
      phoneNumber: storedNumber,
    });
  }

  private getStoredPhoneCountryCode(): string {
    if (!this.data?.phoneNumber) {
      return '';
    }

    const [code] = this.data.phoneNumber.trim().split(/\s+/);
    return code ?? '';
  }

  private getStoredPhoneNumber(): string {
    if (!this.data?.phoneNumber) {
      return '';
    }

    const [, ...rest] = this.data.phoneNumber.trim().split(/\s+/);
    return rest.join(' ');
  }

  private hasChanges(): boolean {
    const raw = this.form.getRawValue();

    const initialCountryId =
      this.countries.find((country) => country.phoneCode === this.getStoredPhoneCountryCode())
        ?.id ?? null;

    const initialPhoneNumber = this.getStoredPhoneNumber();

    return (
      raw.firstName !== (this.data.firstName ?? '') ||
      raw.lastName !== (this.data.lastName ?? '') ||
      raw.email !== (this.data.email ?? '') ||
      raw.countryId !== initialCountryId ||
      raw.phoneNumber !== initialPhoneNumber ||
      raw.isActive !== (this.data.isActive ?? false) ||
      raw.remarks !== (this.data.remarks ?? '') ||
      raw.loyaltyId !== (this.data.loyaltyId ?? '')
    );
  }

  public handleCustomerDetele(): void {
    this.dialogRef.close({ action: 'deleteClicked' });
  }
}
